/**
 * 65. Production Orders — list; detail; schedule; release; start;
 * report consumption/finish; variance.
 */
import {
  Card,
  PageHeader,
  DataTable,
  Skeleton,
  StatusBadge,
  Badge,
} from "@/components/ui";
import { useApiList, useApiQuery } from "@/lib/api/hooks";
import { CreateRecordButton } from "@/components/CreateRecordButton";
import { formatQuantity, formatDate } from "@/lib/money";

interface ProductionOrderRow {
  id: string;
  orderNumber: string;
  productSku: string;
  productName: string;
  quantity: string;
  unitOfMeasure: string;
  startDate: string | null;
  dueDate: string | null;
  bomVersion: number | null;
  status:
    | "Planned"
    | "Released"
    | "Started"
    | "InProgress"
    | "Completed"
    | "Closed"
    | "Cancelled";
}

interface Option {
  id: string;
  name?: string;
  sku?: string;
  code?: string;
}

export function ProductionOrdersPage() {
  const { data, isLoading } = useApiList<ProductionOrderRow>(
    ["manufacturing", "production-orders"],
    "/manufacturing/production-orders",
  );
  // The pickers are loaded here rather than typed as raw uuids: asking a
  // planner to paste a product id is not a form, it is a database console.
  const companies = useApiQuery<Option[]>(["companies"], "/companies");
  const products = useApiQuery<Option[]>(["products"], "/products");
  const uoms = useApiQuery<Option[]>(["uoms"], "/uoms?limit=100");
  const boms = useApiQuery<Option[]>(["boms"], "/boms");
  const warehouses = useApiQuery<Option[]>(["warehouses"], "/warehouses");

  return (
    <div>
      <PageHeader
        title="Production Orders"
        subtitle="Schedule, release, start, consume materials, complete, and close production."
        actions={
          <CreateRecordButton
            label="New production order"
            path="/production-orders"
            invalidate={["manufacturing", "production-orders"]}
            idempotent
            fields={[
              {
                name: "companyId",
                label: "Company",
                type: "select",
                required: true,
                options: (companies.data ?? []).map((c) => ({
                  value: c.id,
                  label: c.name ?? c.code ?? c.id,
                })),
              },
              {
                name: "productId",
                label: "Product to make",
                type: "select",
                required: true,
                options: (products.data ?? []).map((p) => ({
                  value: p.id,
                  label: p.sku
                    ? `${p.sku} — ${p.name ?? ""}`
                    : (p.name ?? p.id),
                })),
              },
              {
                name: "quantity",
                label: "Quantity",
                type: "number",
                required: true,
              },
              {
                name: "unitOfMeasureId",
                label: "Unit",
                type: "select",
                required: true,
                options: (uoms.data ?? []).map((u) => ({
                  value: u.id,
                  label: u.code
                    ? `${u.code} — ${u.name ?? ""}`
                    : (u.name ?? u.id),
                })),
              },
              {
                name: "bomId",
                label: "Bill of materials",
                type: "select",
                hint: "Chosen here, its components become the order's material lines.",
                options: (boms.data ?? []).map((b) => ({
                  value: b.id,
                  label: b.name ?? b.id,
                })),
              },
              {
                name: "warehouseId",
                label: "Finished goods warehouse",
                type: "select",
                hint: "Where the completed units are received.",
                options: (warehouses.data ?? []).map((w) => ({
                  value: w.id,
                  label: w.name ?? w.code ?? w.id,
                })),
              },
            ]}
          />
        }
      />
      {isLoading || !data ? (
        <Skeleton rows={10} />
      ) : (
        <Card title={`Production orders (${data.total})`}>
          <DataTable<ProductionOrderRow>
            rows={data.items}
            columns={[
              {
                key: "orderNumber",
                label: "Number",
                render: (r) => (
                  <span className="atlas-mono">{r.orderNumber}</span>
                ),
              },
              {
                key: "productSku",
                label: "Product",
                render: (r) => (
                  <span className="atlas-mono">{r.productSku}</span>
                ),
              },
              {
                key: "quantity",
                label: "Qty",
                numeric: true,
                render: (r) => formatQuantity(r.quantity),
              },
              { key: "unitOfMeasure", label: "UoM" },
              {
                key: "startDate",
                label: "Start",
                render: (r) => (r.startDate ? formatDate(r.startDate) : "—"),
              },
              {
                key: "dueDate",
                label: "Due",
                render: (r) => (r.dueDate ? formatDate(r.dueDate) : "—"),
              },
              {
                key: "bomVersion",
                label: "BOM",
                render: (r) =>
                  r.bomVersion !== null ? `v${r.bomVersion}` : "—",
              },
              {
                key: "status",
                label: "Status",
                render: (r) =>
                  r.status === "Completed" || r.status === "Closed" ? (
                    <StatusBadge status={r.status} />
                  ) : (
                    <Badge
                      tone={
                        r.status === "Cancelled"
                          ? "danger"
                          : r.status === "InProgress" || r.status === "Started"
                            ? "primary"
                            : "warning"
                      }
                    >
                      {r.status}
                    </Badge>
                  ),
              },
            ]}
          />
        </Card>
      )}
    </div>
  );
}
